/**
 * Componente de configuração do gatilho de Filtro Avançado CRM
 * Permite configurar filtros complexos para disparo automático de fluxos
 * baseado em deals/conversas/tarefas que atendem aos critérios + tempo no status
 *
 * RELEASE: 1.0.0 - 2026-01-25 - Novo componente
 * RELEASE: 1.1.0 - 2026-01-25 - Adicionados filtros para conversas
 * RELEASE: 1.3.0 - 2026-05-31 - Adicionado suporte a entity_type='task'
 * RELEASE: 1.4.0 - 2026-05-31 - Prop lockedEntityType: oculta o seletor de entidade
 *                              e força entity_type quando o gatilho é aberto via
 *                              os virtuais crm_filter_deal/conversation/task na
 *                              sidebar do modal de gatilhos.
 */


import { useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Filter, 
  Clock, 
  Building2, 
  Columns, 
  Users, 
  Tag, 
  AlertCircle,
  Briefcase,
  MessageSquare,
  UserCheck,
  ListChecks,
  FolderTree,
  Flag,
  CheckCircle2
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useTenantMembers } from '@/hooks/useTenantMembers';
import { useCrmWorkspacesHierarchy } from '@/hooks/useCrmWorkspacesHierarchy';
import { useCrmSellers } from '@/hooks/useCrmSellers';
import { useDealTags } from '@/hooks/useDealTags';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CrmFilterTriggerConfig as CrmFilterConfig } from '@/types/flows';
// v1.5.0 - Reaproveitamento dos filtros rápidos das listagens (padrão visual do sistema)
import {
  CrmWorkspaceFilter,
  CrmSellerFilter,
  CrmUserFilter,
  CrmDealTagsFilter,
} from '@/components/crm/filters';

interface CrmFilterTriggerConfigProps {
  config: Partial<CrmFilterConfig>;
  onChange: (config: Partial<CrmFilterConfig>) => void;
  /**
   * v1.4.0 - 2026-05-31
   * Quando definido, o entity_type é travado a partir do tipo virtual escolhido
   * na sidebar do modal (Negócios / Conversas / Tarefas). Nesse modo, o seletor
   * "Tipo de Entidade" deixa de ser exibido (informação fica no cabeçalho).
   */
  lockedEntityType?: 'deal' | 'conversation' | 'task';
}

/**
 * Componente para configurar gatilho de filtro avançado CRM
 * Permite configurar filtros complexos com verificação de tempo no status
 */
export function CrmFilterTriggerConfig({ config, onChange, lockedEntityType }: CrmFilterTriggerConfigProps) {
  const { currentTenant } = useAuth();

  // Quando o tipo é travado pela sidebar (modo virtual), sincronizamos o
  // entity_type via efeito para evitar setState durante render do pai.
  useEffect(() => {
    if (lockedEntityType && config.entity_type !== lockedEntityType) {
      onChange({ ...config, entity_type: lockedEntityType });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedEntityType]);


  
  // =========================================================================
  // HOOKS DE DADOS CRM (DEALS)
  // =========================================================================
  const { workspaces, isLoading: loadingWorkspaces } = useCrmWorkspacesHierarchy();
  const { activeSellers, isLoading: loadingSellers } = useCrmSellers();
  const { data: dealTags = [], isLoading: loadingTags } = useDealTags(currentTenant?.id);
  
  // Buscar fases do pipeline selecionado (para deals)
  const { data: phases = [], isLoading: loadingPhases } = useQuery({
    queryKey: ['crm-phases', config.pipeline_id],
    queryFn: async () => {
      if (!config.pipeline_id) return [];
      const { data, error } = await supabase
        .from('crm_pipeline_phases')
        .select('id, name, color, sort_order')
        .eq('pipeline_id', config.pipeline_id)
        .order('sort_order');
      if (error) throw error;
      return data || [];
    },
    enabled: !!config.pipeline_id,
  });
  
  // v2.0.0: Agentes do tenant via hook centralizado (cache compartilhado, RPC server-side)
  const { data: tenantMembers = [], isLoading: loadingAgents } = useTenantMembers({ onlyActive: true });
  const agents = tenantMembers.map(m => ({
    id: m.user_id,
    name: m.full_name || 'Sem nome',
    avatar_url: m.avatar_url,
  }));

  // =========================================================================
  // HOOKS DE DADOS ESPECÍFICOS PARA CONVERSAS (v1.1.0)
  // =========================================================================
  
  // Buscar departamentos do tenant
  const { data: departments = [] } = useQuery({
    queryKey: ['departments', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data, error } = await supabase
        .from('departments')
        .select('id, name')
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentTenant?.id && config.entity_type === 'conversation',
  });
  
  // Buscar categorias do Kanban de conversas
  const { data: kanbanCategories = [] } = useQuery({
    queryKey: ['kanban-categories', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data, error } = await supabase
        .from('kanban_categories')
        .select('id, name')
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentTenant?.id && config.entity_type === 'conversation',
  });
  
  // Buscar fases do Kanban (baseado na categoria selecionada)
  const { data: kanbanPhases = [] } = useQuery({
    queryKey: ['kanban-phases', config.kanban_category_id],
    queryFn: async () => {
      if (!config.kanban_category_id) return [];
      const { data, error } = await supabase
        .from('kanban_phases')
        .select('id, name, color, position')
        .eq('category_id', config.kanban_category_id)
        .order('position');
      if (error) throw error;
      return data || [];
    },
    enabled: !!config.kanban_category_id,
  });
  
  // Buscar tags de contato
  const { data: contactTags = [] } = useQuery({
    queryKey: ['contact-tags', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data, error } = await supabase
        .from('contact_tags')
        .select('id, name, color')
        .eq('tenant_id', currentTenant.id)
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentTenant?.id && config.entity_type === 'conversation',
  });

  // =========================================================================
  // HOOKS DE DADOS ESPECÍFICOS PARA TAREFAS (v1.3.0)
  // =========================================================================

  // Categorias de tarefa do tenant
  const { data: taskCategories = [] } = useQuery({
    queryKey: ['task-categories-trigger', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data, error } = await supabase
        .from('task_categories')
        .select('id, name, color')
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentTenant?.id && config.entity_type === 'task',
  });

  // Fases (colunas) da categoria de tarefa selecionada
  const { data: taskPhases = [] } = useQuery({
    queryKey: ['task-phases-trigger', config.task_category_id],
    queryFn: async () => {
      if (!config.task_category_id) return [];
      const { data, error } = await supabase
        .from('task_phases')
        .select('id, name, color, sort_order')
        .eq('category_id', config.task_category_id)
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data || [];
    },
    enabled: !!config.task_category_id && config.entity_type === 'task',
  });

  // Classificações da categoria selecionada (opcional)
  const { data: taskClassifications = [] } = useQuery({
    queryKey: ['task-classifications-trigger', config.task_category_id],
    queryFn: async () => {
      if (!config.task_category_id) return [];
      const { data, error } = await supabase
        .from('task_classifications')
        .select('id, name, color')
        .eq('category_id', config.task_category_id)
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data || [];
    },
    enabled: !!config.task_category_id && config.entity_type === 'task',
  });

  // Pipeline selecionado (derivado do workspace)
  const selectedWorkspace = workspaces.find(w => w.id === config.workspace_id);
  const pipelines = selectedWorkspace?.pipelines || [];

  /**
   * Handler genérico para atualizar config
   */
  const updateConfig = (updates: Partial<CrmFilterConfig>) => {
    onChange({ ...config, ...updates });
  };

  /**
   * Toggle seleção de fase (deals)
   */
  const togglePhase = (phaseId: string, phaseName: string) => {
    const currentIds = config.phase_ids || [];
    const currentNames = config.phase_names || [];
    
    if (currentIds.includes(phaseId)) {
      updateConfig({
        phase_ids: currentIds.filter(id => id !== phaseId),
        phase_names: currentNames.filter((_, i) => currentIds[i] !== phaseId)
      });
    } else {
      updateConfig({
        phase_ids: [...currentIds, phaseId],
        phase_names: [...currentNames, phaseName]
      });
    }
  };

  /**
   * Toggle seleção de tag de deal
   */
  const toggleDealTag = (tagId: string, tagName: string) => {
    const currentIds = config.deal_tag_ids || [];
    const currentNames = config.deal_tag_names || [];
    
    if (currentIds.includes(tagId)) {
      updateConfig({
        deal_tag_ids: currentIds.filter(id => id !== tagId),
        deal_tag_names: currentNames.filter((_, i) => currentIds[i] !== tagId)
      });
    } else {
      updateConfig({
        deal_tag_ids: [...currentIds, tagId],
        deal_tag_names: [...currentNames, tagName]
      });
    }
  };
  
  /**
   * Toggle seleção de fase Kanban (conversas) - v1.1.0
   */
  const toggleKanbanPhase = (phaseId: string, phaseName: string) => {
    const currentIds = config.kanban_phase_ids || [];
    const currentNames = config.kanban_phase_names || [];
    
    if (currentIds.includes(phaseId)) {
      updateConfig({
        kanban_phase_ids: currentIds.filter(id => id !== phaseId),
        kanban_phase_names: currentNames.filter((_, i) => currentIds[i] !== phaseId)
      });
    } else {
      updateConfig({
        kanban_phase_ids: [...currentIds, phaseId],
        kanban_phase_names: [...currentNames, phaseName]
      });
    }
  };
  
  /**
   * Toggle seleção de tag de contato (conversas) - v1.1.0
   */
  const toggleContactTag = (tagId: string, tagName: string) => {
    const currentIds = config.contact_tag_ids || [];
    const currentNames = config.contact_tag_names || [];
    
    if (currentIds.includes(tagId)) {
      updateConfig({
        contact_tag_ids: currentIds.filter(id => id !== tagId),
        contact_tag_names: currentNames.filter((_, i) => currentIds[i] !== tagId)
      });
    } else {
      updateConfig({
        contact_tag_ids: [...currentIds, tagId],
        contact_tag_names: [...currentNames, tagName]
      });
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Aviso — padrão SaaS com barra lateral de acento */}
      <div className="md:col-span-2 flex gap-3 rounded-lg border border-border bg-card px-4 py-3 border-l-[3px] border-l-amber-500">
        <Clock className="h-4 w-4 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
        <div className="space-y-0.5">
          <p className="text-sm font-semibold text-foreground">Gatilho baseado em tempo</p>
          <p className="text-sm text-muted-foreground">
            Este gatilho é verificado automaticamente a cada 1 minuto pelo sistema. O fluxo será
            disparado para cada registro que atender aos filtros e estiver no status/fase pelo
            tempo mínimo configurado.
          </p>
        </div>
      </div>

      {/* Tipo de Entidade — oculto quando o tipo já está travado pela sidebar (modo virtual) */}
      {!lockedEntityType && (
        <div className="md:col-span-2 space-y-2">
          <Label className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Tipo de Entidade
          </Label>
          <Select
            value={config.entity_type || 'deal'}
            onValueChange={(value) => updateConfig({
              entity_type: value as 'deal' | 'conversation' | 'task',
              // Limpar filtros ao mudar tipo (inclui task_* — v1.3.0)
              workspace_id: undefined,
              pipeline_id: undefined,
              phase_ids: undefined,
              seller_id: undefined,
              task_category_id: undefined,
              task_category_name: undefined,
              task_phase_ids: undefined,
              task_phase_names: undefined,
              task_classification_id: undefined,
              task_classification_name: undefined,
              task_priority: undefined,
              task_status: undefined,
              task_assigned_to: undefined,
              task_assigned_to_name: undefined,
            })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione o tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="deal">
                <span className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Negócio (CRM)
                </span>
              </SelectItem>
              <SelectItem value="conversation">
                <span className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Conversa (Chat)
                </span>
              </SelectItem>
              <SelectItem value="task">
                <span className="flex items-center gap-2">
                  <ListChecks className="h-4 w-4" />
                  Tarefa
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}



      {/* Filtros para Deals */}
      {(config.entity_type || 'deal') === 'deal' && (
        <>
          {/* Workspace — reaproveita CrmWorkspaceFilter (padrão das listagens) */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Workspace
            </Label>
            <CrmWorkspaceFilter
              value={config.workspace_id || null}
              onChange={(workspaceId) => {
                const ws = workspaces.find(w => w.id === workspaceId);
                updateConfig({
                  workspace_id: workspaceId || undefined,
                  workspace_name: ws?.name,
                  pipeline_id: undefined,
                  pipeline_name: undefined,
                  phase_ids: undefined,
                  phase_names: undefined,
                });
              }}
              className="w-full justify-between h-10 [&>span]:!max-w-none [&>span]:!inline"
            />
          </div>

          {/* Pipeline (condicional ao workspace) */}
          {config.workspace_id && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Columns className="h-4 w-4" />
                Pipeline
              </Label>
              <Select
                value={config.pipeline_id || '__all__'}
                onValueChange={(value) => {
                  const actualValue = value === '__all__' ? undefined : value;
                  const pip = pipelines.find(p => p.id === actualValue);
                  updateConfig({ 
                    pipeline_id: actualValue,
                    pipeline_name: pip?.name,
                    phase_ids: undefined,
                    phase_names: undefined
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos os pipelines" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos os pipelines</SelectItem>
                  {pipelines.map(pip => (
                    <SelectItem key={pip.id} value={pip.id}>{pip.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Fases (multi-select) */}
          {config.pipeline_id && phases.length > 0 && (
            <div className="md:col-span-2 space-y-2">
              <Label>Fases (selecione uma ou mais)</Label>
              <ScrollArea className="h-32 border rounded-lg p-2">
                <div className="space-y-2">
                  {phases.map(phase => (
                    <div 
                      key={phase.id} 
                      className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer"
                      onClick={() => togglePhase(phase.id, phase.name)}
                    >
                      <Checkbox 
                        checked={config.phase_ids?.includes(phase.id)}
                        onCheckedChange={() => togglePhase(phase.id, phase.name)}
                      />
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: phase.color || '#6366f1' }}
                      />
                      <span className="text-sm">{phase.name}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              {config.phase_ids && config.phase_ids.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {config.phase_names?.map((name, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">
                      {name}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Vendedor — reaproveita CrmSellerFilter (padrão das listagens) */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Vendedor
            </Label>
            <CrmSellerFilter
              value={config.seller_id || null}
              onChange={(sellerId) => {
                const seller = activeSellers?.find(s => s.id === sellerId);
                updateConfig({
                  seller_id: sellerId || undefined,
                  seller_name: seller?.name || undefined,
                });
              }}
              label="Qualquer vendedor"
              className="w-full justify-between h-10 [&>span]:!max-w-none [&>span]:!inline"
            />
          </div>

          {/* Responsável (Negócio) — reaproveita CrmUserFilter + checkbox auxiliar p/ "Sem responsável" */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Responsável
            </Label>
            <CrmUserFilter
              value={config.assigned_to && config.assigned_to !== '__none__' ? config.assigned_to : null}
              onChange={(userId) => {
                const agent = agents.find(a => a.id === userId);
                updateConfig({
                  assigned_to: userId || undefined,
                  assigned_to_name: agent?.name || undefined,
                });
              }}
              label="Qualquer responsável"
              allLabel="Qualquer responsável"
              className="w-full justify-between h-10 [&>span]:!max-w-none [&>span]:!inline"
            />
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
              <Checkbox
                checked={config.assigned_to === '__none__'}
                onCheckedChange={(checked) => {
                  updateConfig({
                    assigned_to: checked ? '__none__' : undefined,
                    assigned_to_name: checked ? 'Nenhum' : undefined,
                  });
                }}
              />
              <span>Apenas negócios sem responsável (nenhum atribuído)</span>
            </label>
          </div>


          {/* Status do Deal */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Status do Negócio
            </Label>
            <Select
              value={config.status || 'open'}
              onValueChange={(value) => updateConfig({ status: value as 'open' | 'won' | 'lost' })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Em aberto</SelectItem>
                <SelectItem value="won">Ganho</SelectItem>
                <SelectItem value="lost">Perdido</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Tags do Negócio — reaproveita CrmDealTagsFilter (padrão das listagens) */}
          {dealTags.length > 0 && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Tags do Negócio (opcional)
              </Label>
              <CrmDealTagsFilter
                value={config.deal_tag_ids || []}
                onChange={(tagIds) => {
                  // Resolver nomes a partir do hook useDealTags já carregado
                  const tagNames = tagIds
                    .map(id => dealTags.find((t: any) => t.id === id)?.name)
                    .filter(Boolean) as string[];
                  updateConfig({
                    deal_tag_ids: tagIds.length > 0 ? tagIds : undefined,
                    deal_tag_names: tagIds.length > 0 ? tagNames : undefined,
                  });
                }}
                className="w-full justify-between h-10"
              />
            </div>
          )}

        </>
      )}

      {/* ===================================================================== */}
      {/* FILTROS PARA CONVERSAS - v1.1.0                                      */}
      {/* ===================================================================== */}
      {config.entity_type === 'conversation' && (
        <>
          {/* Status da Conversa */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Status da Conversa
            </Label>
            <Select
              value={config.conversation_status || '__any__'}
              onValueChange={(value) => updateConfig({ 
                conversation_status: value as 'pending' | 'open' | 'closed' | '__any__'
              })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {/* RELEASE: 1.2.0 - Adicionado opção "Qualquer status" */}
                <SelectItem value="__any__">Qualquer status</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
                <SelectItem value="open">Em aberto</SelectItem>
                <SelectItem value="closed">Encerrado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Departamento */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Departamento
            </Label>
            <Select
              value={config.department_id || '__any__'}
              onValueChange={(value) => {
                // RELEASE: 1.2.0 - Suporte a '__none__' (sem departamento) e '__any__' (qualquer)
                const isNone = value === '__none__';
                const isAny = value === '__any__';
                const actual = (isNone || isAny) ? value : value;
                const dept = (!isNone && !isAny) ? departments.find(d => d.id === actual) : undefined;
                updateConfig({ 
                  department_id: isAny ? undefined : actual,
                  department_name: isNone ? 'Nenhum' : (isAny ? undefined : dept?.name)
                });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Qualquer departamento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">Qualquer departamento</SelectItem>
                <SelectItem value="__none__">Nenhum (sem departamento)</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Agente Responsável — reaproveita CrmUserFilter + checkbox auxiliar p/ "Sem atribuição" */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <UserCheck className="h-4 w-4" />
              Agente Responsável
            </Label>
            <CrmUserFilter
              value={config.assigned_to && config.assigned_to !== '__none__' ? config.assigned_to : null}
              onChange={(userId) => {
                const agent = agents.find(a => a.id === userId);
                updateConfig({
                  assigned_to: userId || undefined,
                  assigned_to_name: agent?.name || undefined,
                });
              }}
              label="Qualquer agente"
              allLabel="Qualquer agente"
              groupLabel="Agentes"
              className="w-full justify-between h-10 [&>span]:!max-w-none [&>span]:!inline"
            />
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
              <Checkbox
                checked={config.assigned_to === '__none__'}
                onCheckedChange={(checked) => {
                  updateConfig({
                    assigned_to: checked ? '__none__' : undefined,
                    assigned_to_name: checked ? 'Nenhum' : undefined,
                  });
                }}
              />
              <span>Apenas conversas sem atribuição (nenhum agente)</span>
            </label>
          </div>

          {/* Categoria Kanban */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Columns className="h-4 w-4" />
              Categoria Kanban
            </Label>
            <Select
              value={config.kanban_category_id || '__all__'}
              onValueChange={(value) => {
                const actual = value === '__all__' ? undefined : value;
                const cat = kanbanCategories.find(c => c.id === actual);
                updateConfig({ 
                  kanban_category_id: actual,
                  kanban_category_name: cat?.name,
                  kanban_phase_ids: undefined,
                  kanban_phase_names: undefined
                });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Qualquer categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Qualquer categoria</SelectItem>
                {kanbanCategories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fases Kanban (multi-select) */}
          {config.kanban_category_id && kanbanPhases.length > 0 && (
            <div className="md:col-span-2 space-y-2">
              <Label>Fases do Kanban (selecione uma ou mais)</Label>
              <ScrollArea className="h-32 border rounded-lg p-2">
                <div className="space-y-2">
                  {kanbanPhases.map(phase => (
                    <div 
                      key={phase.id} 
                      className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer"
                      onClick={() => toggleKanbanPhase(phase.id, phase.name)}
                    >
                      <Checkbox 
                        checked={config.kanban_phase_ids?.includes(phase.id)}
                        onCheckedChange={() => toggleKanbanPhase(phase.id, phase.name)}
                      />
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: phase.color || '#6366f1' }}
                      />
                      <span className="text-sm">{phase.name}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              {config.kanban_phase_ids && config.kanban_phase_ids.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {config.kanban_phase_names?.map((name, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">
                      {name}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tags do Contato (multi-select) */}
          {contactTags.length > 0 && (
            <div className="md:col-span-2 space-y-2">
              <Label className="flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Tags do Contato (opcional)
              </Label>
              <ScrollArea className="h-32 border rounded-lg p-2">
                <div className="space-y-2">
                  {contactTags.map(tag => (
                    <div 
                      key={tag.id} 
                      className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer"
                      onClick={() => toggleContactTag(tag.id, tag.name)}
                    >
                      <Checkbox 
                        checked={config.contact_tag_ids?.includes(tag.id)}
                        onCheckedChange={() => toggleContactTag(tag.id, tag.name)}
                      />
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: tag.color || '#6366f1' }}
                      />
                      <span className="text-sm">{tag.name}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              {config.contact_tag_ids && config.contact_tag_ids.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {config.contact_tag_names?.map((name, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {name}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ===================================================================== */}
      {/* FILTROS PARA TAREFAS - v1.3.0                                         */}
      {/* ===================================================================== */}
      {config.entity_type === 'task' && (
        <>
          {/* Categoria de Tarefa */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <FolderTree className="h-4 w-4" />
              Categoria
            </Label>
            <Select
              value={config.task_category_id || '__all__'}
              onValueChange={(value) => {
                const actual = value === '__all__' ? undefined : value;
                const cat = taskCategories.find(c => c.id === actual);
                updateConfig({
                  task_category_id: actual,
                  task_category_name: cat?.name,
                  task_phase_ids: undefined,
                  task_phase_names: undefined,
                  task_classification_id: undefined,
                  task_classification_name: undefined,
                });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Qualquer categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Qualquer categoria</SelectItem>
                {taskCategories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fases (multi-select) condicional à categoria */}
          {config.task_category_id && taskPhases.length > 0 && (
            <div className="md:col-span-2 space-y-2">
              <Label className="flex items-center gap-2">
                <Columns className="h-4 w-4" />
                Fases (selecione uma ou mais)
              </Label>
              <ScrollArea className="h-32 border rounded-lg p-2">
                <div className="space-y-2">
                  {taskPhases.map(phase => {
                    const ids = config.task_phase_ids || [];
                    const names = config.task_phase_names || [];
                    const checked = ids.includes(phase.id);
                    const toggle = () => {
                      if (checked) {
                        updateConfig({
                          task_phase_ids: ids.filter(id => id !== phase.id),
                          task_phase_names: names.filter((_, i) => ids[i] !== phase.id),
                        });
                      } else {
                        updateConfig({
                          task_phase_ids: [...ids, phase.id],
                          task_phase_names: [...names, phase.name],
                        });
                      }
                    };
                    return (
                      <div
                        key={phase.id}
                        className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer"
                        onClick={toggle}
                      >
                        <Checkbox checked={checked} onCheckedChange={toggle} />
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: phase.color || '#6366f1' }}
                        />
                        <span className="text-sm">{phase.name}</span>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
              {config.task_phase_ids && config.task_phase_ids.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {config.task_phase_names?.map((name, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">{name}</Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Classificação opcional */}
          {config.task_category_id && taskClassifications.length > 0 && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Classificação (opcional)
              </Label>
              <Select
                value={config.task_classification_id || '__all__'}
                onValueChange={(value) => {
                  const actual = value === '__all__' ? undefined : value;
                  const cls = taskClassifications.find(c => c.id === actual);
                  updateConfig({
                    task_classification_id: actual,
                    task_classification_name: cls?.name,
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Qualquer classificação" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Qualquer classificação</SelectItem>
                  {taskClassifications.map(cls => (
                    <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Status da Tarefa
              v1.3.1 (2026-05-31): default mudou para 'open' (qualquer aberta — pending/in_progress/paused),
              evitando descartar tarefas em pending no scheduler. */}
          <div className="space-y-2">
            <Label>Status da Tarefa</Label>
            <Select
              value={config.task_status || (config as any).status || 'open'}
              onValueChange={(value) => updateConfig({
                task_status: value as CrmFilterConfig['task_status'],
                // Mantém compatibilidade com chave legada 'status' lida pelo scheduler v2.0.1
                status: value as any,
              })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Qualquer aberta (pendente, em andamento ou pausada)</SelectItem>
                <SelectItem value="__any__">Qualquer status (inclui concluída/cancelada)</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
                <SelectItem value="in_progress">Em andamento</SelectItem>
                <SelectItem value="paused">Pausada</SelectItem>
                <SelectItem value="completed">Concluída</SelectItem>
                <SelectItem value="cancelled">Cancelada</SelectItem>
              </SelectContent>
            </Select>
          </div>


          {/* Prioridade */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Flag className="h-4 w-4" />
              Prioridade (opcional)
            </Label>
            <Select
              value={config.task_priority || '__any__'}
              onValueChange={(value) => updateConfig({
                task_priority: value === '__any__' ? undefined : (value as CrmFilterConfig['task_priority'])
              })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">Qualquer prioridade</SelectItem>
                <SelectItem value="low">Baixa</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="high">Alta</SelectItem>
                <SelectItem value="urgent">Urgente</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Responsável (Tarefa) — reaproveita CrmUserFilter + checkbox auxiliar p/ "Sem atribuição" */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <UserCheck className="h-4 w-4" />
              Responsável
            </Label>
            <CrmUserFilter
              value={config.task_assigned_to && config.task_assigned_to !== '__none__' ? config.task_assigned_to : null}
              onChange={(userId) => {
                const agent = agents.find(a => a.id === userId);
                updateConfig({
                  task_assigned_to: userId || undefined,
                  task_assigned_to_name: agent?.name || undefined,
                });
              }}
              label="Qualquer responsável"
              allLabel="Qualquer responsável"
              className="w-full justify-between h-10 [&>span]:!max-w-none [&>span]:!inline"
            />
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
              <Checkbox
                checked={config.task_assigned_to === '__none__'}
                onCheckedChange={(checked) => {
                  updateConfig({
                    task_assigned_to: checked ? '__none__' : undefined,
                    task_assigned_to_name: checked ? 'Nenhum' : undefined,
                  });
                }}
              />
              <span>Apenas tarefas sem atribuição (nenhum responsável)</span>
            </label>
          </div>
        </>
      )}


      {/* Seção: Tempo no Status - OBRIGATÓRIO */}
      <div className="md:col-span-2 space-y-4 p-4 border rounded-lg bg-muted/30">
        <Label className="flex items-center gap-2 text-base font-semibold">
          <Clock className="h-5 w-5 text-amber-500" />
          Tempo Mínimo no Status Atual
        </Label>
        <p className="text-sm text-muted-foreground">
          O fluxo será disparado apenas se a entidade estiver no status/fase 
          atual há mais tempo que o configurado abaixo.
        </p>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Quantidade</Label>
            <Input
              type="number"
              min={1}
              value={config.time_in_status || 1}
              onChange={(e) => updateConfig({ 
                time_in_status: parseInt(e.target.value) || 1 
              })}
            />
          </div>
          <div className="space-y-2">
            <Label>Unidade</Label>
            <Select
              value={config.time_unit || 'days'}
              onValueChange={(value) => updateConfig({ 
                time_unit: value as 'minutes' | 'hours' | 'days' | 'weeks' 
              })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="minutes">Minutos</SelectItem>
                <SelectItem value="hours">Horas</SelectItem>
                <SelectItem value="days">Dias</SelectItem>
                <SelectItem value="weeks">Semanas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Preview do tempo configurado */}
        <div className="p-3 bg-background border rounded text-sm">
          <span className="text-muted-foreground">Condição: </span>
          <span className="font-medium">
            {config.entity_type === 'conversation' ? 'Conversa' : (config.entity_type === 'task' ? 'Tarefa' : 'Negócio')} 
            {' '}no status atual há mais de{' '}
            <span className="text-amber-600 font-bold">
              {config.time_in_status || 1} {
                config.time_unit === 'minutes' ? 'minuto(s)' :
                config.time_unit === 'hours' ? 'hora(s)' :
                config.time_unit === 'weeks' ? 'semana(s)' :
                'dia(s)'
              }
            </span>
          </span>
        </div>
      </div>

      {/* Aviso sobre execução única */}
      <Alert className="md:col-span-2">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Por padrão, cada registro dispara o fluxo <strong>apenas uma vez</strong> 
          enquanto permanecer na mesma configuração de filtros. Se você alterar 
          os filtros, os registros poderão ser processados novamente.
        </AlertDescription>
      </Alert>
    </div>
  );
}